// [APP-FORK] 앱 유도 가드 — ProviderRouter.tsx 의 /:storeNo · /gift/:storeNo/:giftNo ·
//   claim/:storeNo/:purchaseNo/:uuid 3개 라우트에 걸린다.
//
// 정책: PC 웹은 전체 flow(선물받기 포함)를 지원하므로 차단하지 않는다.
//   모바일 브라우저만 앱으로 유도한다.
//
// 차단 조건은 !isApp() && isMobile() 이다(utils/isApp.ts). isApp() 은 UA 의 'naegiftApp/1.0'
//   마커를, isMobile() 은 android/iphone/ipod/windows phone 을 본다.
//   ★도메인을 보지 않는 이유: 앱 웹뷰와 웹 브라우저가 같은 도메인·같은 번들을 쓴다
//     (shopuser-dev/-qa.naegift.com · shopuser.naegift.com — 앱 전용 도메인이 없어졌다).
//     도메인으로는 둘을 가려낼 수 없다.
//   ★인앱브라우저 UA 를 보지 않는 이유: "앱 아닌 모바일이면 전부"가 정책이라 조건에 불필요하고,
//     PC 카카오는 인앱브라우저가 아니라 시스템 브라우저로 열려 이제 통과 대상이다.
//     (isInAppBrowser 는 아래 안내 문구 분기용으로만 남긴다 — 차단 판정과 무관)
//   ★window.flutter_inappwebview 브릿지를 쓰지 않는 이유: 브릿지는 주입 타이밍에 의존해
//     지연되면 앱 사용자가 차단 화면을 보는 실패 모드가 있다. UA 는 그 실패 모드가 구조적으로
//     없다 — naegift_mobile_app/lib/webview/web_view_page.dart:112-116 이 UA 조회 완료 전엔
//     웹뷰를 만들지 않으므로 마커 없는 로드가 발생하지 않는다
//     (Android = 같은 파일 :133-137 UA 리터럴, iOS = :91 기본 WKWebView UA 끝에 덧붙임).
//
// 배경: 카카오톡 등 인앱브라우저는 자체 WebView 가 https 내비게이션을 가로채 그대로 로드한다.
//   시스템에 ACTION_VIEW 인텐트가 디스패치되지 않으므로 Android App Link
//   (autoVerify, naegift_mobile_app/android/app/src/main/AndroidManifest.xml 의 /claim 필터)가
//   발화하지 않는다 → 앱이 있어도 웹으로 열린다. 그 경우까지 여기서 앱으로 유도한다.
import React from 'react';
import { isApp, isMobile } from '../utils/isApp';
import { storeUrlForOs } from './storeUrls';
// [APP-FORK] intent:// 의 package= 는 도메인 환경별 flavor 패키지로 — 사유는 appPackage.ts 주석.
import { androidAppPackage, iosAppScheme } from './appPackage';

// 인앱브라우저 UA 패턴 — 차단 판정이 아니라 아래 안내 문구 분기용.
//   표준이 아니라 벤더 관행이므로 필요 시 추가한다. 목록이 새도 힌트가 안 뜰 뿐 차단은 정상이다.
//   카카오톡 인앱은 UA 에 'KAKAOTALK' 을 포함한다.
const IN_APP_BROWSER_UA =
  /KAKAOTALK|inapp|FB_IAB|FBAN|FBIOS|Instagram|Line|NAVER|DaumApps|band/i;

function isInAppBrowser(): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return IN_APP_BROWSER_UA.test(ua);
}

interface Props {
  children: React.ReactNode;
}

export default function AppOnlyGuard({ children }: Props): React.JSX.Element {
  // 둘 다 UA 동기 판정이라 첫 렌더에 확정된다 → 차단 시 children 이 아예 마운트되지 않는다.
  //  앱 웹뷰와 PC 브라우저는 통과, 모바일 브라우저만 아래 유도 화면.
  if (isApp() || !isMobile()) return <>{children}</>;

  // Android: intent:// 로 앱 실행 시도. 현재 URL 을 그대로 앱에 넘긴다(App Link 필터와 같은 경로).
  //  ⚠️ 카카오 인앱브라우저에서 intent:// 가 실제로 뜨는지는 보장되지 않는다(카카오 공식 답변상
  //     외부 이동 미지원, 실패 사례 보고 있음).
  //  iOS: intent:// 자체가 없어 {scheme}://claim/{storeNo}/{purchaseNo}/{uuid} 커스텀 스킴으로 대체
  //     (AppDelegate/SceneDelegate 의 handleOpenURL → naegift/deeplink 채널 → app_shell.dart
  //     _handleDeepLink 의 스킴 분기가 처리). scheme 은 iosAppScheme() 로 환경별 분기(dev/qa/prod
  //     앱이 전부 'naegift' 하나만 쓰면 iOS 가 임의로 한 앱만 열어버린다 — androidAppPackage() 와
  //     같은 이유, appPackage.ts 주석 참조). 그래도 실패할 수 있어 아래 폴백 안내는 유지.
  const onOpenApp = (): void => {
    const { host, pathname, search } = window.location;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    if (/iphone|ipad|ipod/i.test(ua)) {
      const segs = pathname.split('/').filter(Boolean); // ['claim', storeNo, purchaseNo, uuid]
      if (segs.length >= 4 && segs[0] === 'claim') {
        window.location.href = `${iosAppScheme()}://claim/${segs[1]}/${segs[2]}/${segs[3]}`;
        return;
      }
    }
    window.location.href = `intent://${host}${pathname}${search}#Intent;scheme=https;package=${androidAppPackage()};end`;
  };

  // 앱 미설치 사용자용 — 스토어로 유도. URL 미등록(빈 문자열)이면 "준비중" 안내.
  //  타이머로 자동 감지하지 않고 버튼 선택으로 처리 → 앱 있는 사용자를 스토어로 오인 보내지 않는다.
  const onInstallApp = (): void => {
    const storeUrl = storeUrlForOs();
    if (storeUrl) {
      window.location.href = storeUrl;
    } else {
      // eslint-disable-next-line no-alert
      window.alert('앱 준비중입니다. 곧 출시됩니다.');
    }
  };

  const wrapStyle: React.CSSProperties = {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 24,
    textAlign: 'center',
  };

  return (
    <div style={wrapStyle}>
      <div style={{ fontSize: 40 }}>🎁</div>
      <p style={{ fontSize: 16, color: '#333' }}>
        내기프트 앱에서 열어주세요
      </p>
      <p style={{ fontSize: 13, color: '#888' }}>
        이 페이지는 내기프트 앱에서 확인하실 수 있어요.
      </p>
      <button
        type="button"
        onClick={onOpenApp}
        style={{
          padding: '10px 20px',
          borderRadius: 8,
          border: 'none',
          background: '#8AAAE5',
          color: '#fff',
          fontSize: 15,
          cursor: 'pointer',
        }}
      >
        앱에서 열기
      </button>
      {/* 앱 미설치 사용자용 스토어 유도(선택 방식, 타이머 없음). */}
      <button
        type="button"
        onClick={onInstallApp}
        style={{
          padding: '10px 20px',
          borderRadius: 8,
          border: '1px solid #8AAAE5',
          background: '#fff',
          color: '#8AAAE5',
          fontSize: 15,
          cursor: 'pointer',
        }}
      >
        앱 설치하기
      </button>
      {/* ⋯ 메뉴 안내는 인앱브라우저에만 있는 기능 — 일반/PC 브라우저엔 거짓 안내라 분기한다.
          intent:// 가 카카오에서 실패할 때(위 onOpenApp 주석) 유일한 탈출구라 유지한다. */}
      {isInAppBrowser() && (
        <p style={{ fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
          앱이 열리지 않으면 우측 상단 ⋯ 메뉴에서
          <br />
          &apos;다른 브라우저로 열기&apos;를 눌러주세요.
        </p>
      )}
    </div>
  );
}
